import React, { useState } from 'react';
import { Calendar, MapPin, Users, User, Phone, Clock, Truck, IdentificationCard, Lightning, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const estadoConfig = {
  agendado: { label: 'Agendado', color: '#6C757D', bgColor: '#6C757D20' },
  en_espera_asignacion: { label: 'En Espera de Asignación', color: '#0891B2', bgColor: '#0891B220' },
  asignado: { label: 'Asignado', color: '#008630', bgColor: '#00863020' },
  realizada: { label: 'Realizada', color: '#065F46', bgColor: '#065F4620' },
  suspendida: { label: 'Suspendida', color: '#78716C', bgColor: '#78716C20' },
  aprobado: { label: 'Aprobado', color: '#C1121F', bgColor: '#C1121F20' },
  rechazado: { label: 'Rechazado', color: '#FF3B30', bgColor: '#FF3B3020' }
};

const EDITABLE_STATES = new Set(['agendado']);
const DELETABLE_STATES = new Set(['agendado']);

export const MovilizacionCard = ({ movilizacion, onEdit, onDelete }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const estado = estadoConfig[movilizacion.estado] || estadoConfig.agendado;
  const canEdit = EDITABLE_STATES.has(movilizacion.estado) && typeof onEdit === 'function';
  const canDelete = DELETABLE_STATES.has(movilizacion.estado) && typeof onDelete === 'function';

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(movilizacion);
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      data-testid={`movilizacion-card-${movilizacion.id}`}
      className="bg-white border border-[#E9ECEF] p-6 hover:border-[#0A0A0A] transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span
              className="px-3 py-1 text-sm font-bold font-['IBM_Plex_Sans']"
              style={{ backgroundColor: estado.bgColor, color: estado.color }}
            >
              {estado.label}
            </span>
            {movilizacion.es_emergente && (
              <span
                data-testid="emergente-tag"
                className="flex items-center gap-1 px-2 py-1 text-xs font-bold bg-[#C1121F] text-white font-['IBM_Plex_Sans']"
              >
                <Lightning size={12} weight="fill" /> EMERGENTE
              </span>
            )}
            <span className="text-xs text-[#6C757D] font-['IBM_Plex_Sans']">
              ID: {movilizacion.id ? movilizacion.id.slice(0, 8) : 'N/A'}
            </span>
          </div>
          {(movilizacion.aprobado_por || movilizacion.rechazado_por) && (
            <p
              data-testid="audit-solicitante"
              className="text-xs font-['IBM_Plex_Sans'] mt-1"
              style={{ color: movilizacion.rechazado_por ? '#FF3B30' : '#0891B2' }}
            >
              <strong>
                {movilizacion.rechazado_por ? 'Rechazado' : 'Aprobado'} por:
              </strong>{' '}
              {movilizacion.rechazado_por || movilizacion.aprobado_por}
            </p>
          )}
        </div>
        {(canEdit || canDelete) && (
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button
                data-testid={`solicitante-edit-${movilizacion.id}`}
                size="sm"
                variant="outline"
                onClick={() => onEdit(movilizacion)}
                className="gap-2 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
              >
                <PencilSimple size={14} weight="bold" /> Editar
              </Button>
            )}
            {canDelete && (
              <Button
                data-testid={`solicitante-delete-${movilizacion.id}`}
                size="sm"
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                className="gap-2 border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] font-['IBM_Plex_Sans']"
              >
                <Trash size={14} weight="bold" /> Eliminar
              </Button>
            )}
          </div>
        )}
      </div>

      {movilizacion.es_emergente && movilizacion.justificacion && (
        <div className="mb-4 p-3 bg-[#C1121F]/10 border border-[#C1121F]">
          <p className="text-xs font-bold uppercase tracking-wide text-[#C1121F] font-['IBM_Plex_Sans'] mb-1">
            Justificación
          </p>
          <p className="text-sm text-[#0A0A0A] font-['IBM_Plex_Sans']">{movilizacion.justificacion}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Calendar size={20} weight="bold" className="text-[#C1121F] mt-0.5" />
            <div>
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Fecha y Hora</p>
              <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">
                {format(new Date(movilizacion.fecha_inicio), "d MMM yyyy, HH:mm", { locale: es })} -
              </p>
              <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">
                {format(new Date(movilizacion.fecha_fin), "d MMM yyyy, HH:mm", { locale: es })}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <MapPin size={20} weight="bold" className="text-[#C1121F] mt-0.5" />
            <div>
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Sitio de Salida</p>
              <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.direccion}</p>
              <p className="text-xs text-[#495057] font-['IBM_Plex_Sans'] mt-1">Ref: {movilizacion.referencia}</p>
            </div>
          </div>

          {movilizacion.direccion_llegada && (
            <div className="flex items-start gap-3">
              <MapPin size={20} weight="bold" className="text-[#008630] mt-0.5" />
              <div>
                <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Lugar de Llegada</p>
                <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.direccion_llegada}</p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3">
            <Users size={20} weight="bold" className="text-[#C1121F] mt-0.5" />
            <div>
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Personas</p>
              <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.num_personas}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <User size={20} weight="bold" className="text-[#C1121F] mt-0.5" />
            <div>
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Responsable</p>
              <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.responsable}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Phone size={20} weight="bold" className="text-[#C1121F] mt-0.5" />
            <div>
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Contacto</p>
              <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.telefono_contacto}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <IdentificationCard size={20} weight="bold" className="text-[#C1121F] mt-0.5" />
            <div>
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Área</p>
              <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.area}</p>
            </div>
          </div>
        </div>
      </div>

      {movilizacion.vehiculo && (
        <div className="mt-4 pt-4 border-t border-[#E9ECEF]">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Truck size={18} weight="bold" className="text-[#C1121F]" />
              <div>
                <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans']">Vehículo</p>
                <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.vehiculo}</p>
              </div>
            </div>
            {movilizacion.conductor && (
              <div className="flex items-center gap-2">
                <User size={18} weight="bold" className="text-[#C1121F]" />
                <div>
                  <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans']">Conductor</p>
                  <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.conductor}</p>
                  {movilizacion.telefono_conductor && (
                    <p className="text-xs text-[#495057] font-['IBM_Plex_Sans']">{movilizacion.telefono_conductor}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {movilizacion.materiales && (
        <div data-testid="materiales-block" className="mt-4 pt-4 border-t border-[#E9ECEF]">
          <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1 uppercase tracking-wide font-bold">Materiales a Transportar</p>
          <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A] whitespace-pre-line">{movilizacion.materiales}</p>
        </div>
      )}

      {movilizacion.comentario_admin && (
        <div className="mt-4 pt-4 border-t border-[#E9ECEF]">
          <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Comentario del Administrador</p>
          <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.comentario_admin}</p>
        </div>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="solicitante-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Chivo']">¿Eliminar esta solicitud?</AlertDialogTitle>
            <AlertDialogDescription className="font-['IBM_Plex_Sans']">
              Esta acción no se puede deshacer. Se eliminará permanentemente tu solicitud en estado Agendado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="solicitante-delete-cancel" disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="solicitante-delete-confirm"
              onClick={(e) => { e.preventDefault(); handleConfirmDelete(); }}
              disabled={deleting}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
            >
              {deleting ? 'Eliminando...' : 'Eliminar Definitivamente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};